import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { notification, Form } from "antd";
import { ShippingTruck } from "assets/icons";
import YmsDrawer from "components/drawer";
import FormCheckin from "components/form/checkin";
import Layout from "components/layout";
import { bindActionCreators } from "redux";
import { Creators as LobbyActions } from "store/lobby/lobby-duck";
import { IDocumentsData, IDocumentType, IProcessesData, IRegisterActiveItem, ISaleChannelData } from "store/lobby/lobby-model";
import { Creators as UserSessionActions } from "store/user-session/user-session-duck";
import { IUserData, MenuItemActive } from "store/user-session/user-session-model";
import { Creators as VehicleDashboardActions } from "store/vehicle-dashboard/vehicle-dashboard-duck";
import { IVehicleDashboardItem } from "store/vehicle-dashboard/vehicle-dashboard-model";
import TokenService from "utils/auth/Token.service";
import useTranslation from "utils/hooks/useTranslation";
import FilterOptions from "./components/FilterOptions";
import {
  InOperationInformations,
  InRadiusInformations,
  InternQueueInformations,
  NoCheckinInRadiusInformations,
  ReleasedNotReleasedInformations,
  VehicleArrivingInformations,
} from "./components/Informations";
import VehiclesCard from "./components/VehiclesCard";
import ViewRegister from "./components/ViewRegister";
import * as S from "./index.styled";

interface IPropsScreenVehicleDashboard {
  isLoading?: boolean;
  userData?: IUserData;
  items?: Record<string, IVehicleDashboardItem>;
  registerActive?: IRegisterActiveItem;
  visibleDrawerViewRegisterVehicleDashboard?: boolean;
  visibleDrawerViewCheckinRegisterVehicleDashboard?: boolean;
  drawerIsLoading?: boolean;
  salesChannelList?: Array<ISaleChannelData>;
  hasSaleChannel?: boolean;
  documentsList?: IDocumentsData[];
  inboundProcessesList?: Array<IProcessesData>;
  documentType?: IDocumentType;
  hasRfidReadingActive?: boolean;
}

interface IDispatchProps {
  vehicleDashboardFetchRegisterAsync?: (...args) => void;
  userSessionMenuItemActive?: (...args) => void;
  lobbySaleChannelsFetchAsync?: (...args) => void;
  lobbyInboundProcessesFetchAsync?: (...args) => void;
  vehicleDashboardHideDrawerViewRegister?: () => void;
  lobbyClearDocuments?: () => void;
  lobbySaleChannelActive?: (...args) => void;
  lobbyProcessActive?: (...args) => void;
  lobbyDocumentsFetchAsync?: (...args) => void;
  lobbyFetchItemAsync?: (...args) => void;
  lobbyFetchItemCheckinAsync?: (...args) => void;
  vehicleDashboardShowDrawerViewCheckinRegister?: (...args) => void;
  vehicleDashboardShowDrawerViewRegister?: () => void;
  vehicleDashboardHideDrawerViewCheckinRegister?: () => void;
  lobbyCheckinClearDrivers?: () => void;
  lobbyCheckinUpdateRegisterActive?: (...args) => void;
  lobbyUpdateRegisterActive?: (...args) => void;
  vehicleDashboardChangeFilterOptionsValue?: (...args) => void;
  vehicleDashboardResetVehiclesData?: () => void;
}

export type IVehicleDashboardProps = IPropsScreenVehicleDashboard & IDispatchProps;

const VehicleDashboard = ({
  items,
  userData,
  isLoading,
  documentType,
  documentsList,
  hasSaleChannel,
  registerActive,
  drawerIsLoading,
  salesChannelList,
  inboundProcessesList,
  hasRfidReadingActive,
  visibleDrawerViewRegisterVehicleDashboard,
  visibleDrawerViewCheckinRegisterVehicleDashboard,
  lobbyProcessActive,
  lobbyClearDocuments,
  lobbyFetchItemAsync,
  lobbySaleChannelActive,
  lobbyCheckinClearDrivers,
  lobbyDocumentsFetchAsync,
  userSessionMenuItemActive,
  lobbyUpdateRegisterActive,
  lobbyFetchItemCheckinAsync,
  lobbySaleChannelsFetchAsync,
  lobbyInboundProcessesFetchAsync,
  lobbyCheckinUpdateRegisterActive,
  vehicleDashboardResetVehiclesData,
  vehicleDashboardFetchRegisterAsync,
  vehicleDashboardShowDrawerViewRegister,
  vehicleDashboardHideDrawerViewRegister,
  vehicleDashboardChangeFilterOptionsValue,
  vehicleDashboardShowDrawerViewCheckinRegister,
  vehicleDashboardHideDrawerViewCheckinRegister,
}: IVehicleDashboardProps) => {
  const TITLE_PAGE_REGISTRATION = useTranslation({ id: "vehicle_dashboard" });
  const RELEASED = useTranslation({ id: "released" });
  const NOT_RELEASED = useTranslation({ id: "not_released" });
  const PRESENTEND = useTranslation({ id: "presented_vehicle" });
  const IN_RADIUS = useTranslation({ id: "in_radius" });
  const NO_CHECK_IN_RADIUS = useTranslation({ id: "no_check_in_radius" });
  const INTERN_QUEUE = useTranslation({ id: "intern_queue" });
  const IN_OPERATION = useTranslation({ id: "in_operation" });
  const VIEW_ENTRY = useTranslation({ id: "view_entry" });
  const CANCEL = useTranslation({ id: "cancel" });
  const [processValue, setProcessValue] = useState([]);
  const [salesChannelValue, setSalesChannelValue] = useState([]);
  const INTERVAL_MINUTES = 60000;
  const tokenService = new TokenService();
  const [form] = Form.useForm();

  const intervalCallApi = () => {
    vehicleDashboardFetchRegisterAsync();
  };

  const resetFilterVehicleDashboard = () => {
    vehicleDashboardChangeFilterOptionsValue("processId", []);
    vehicleDashboardChangeFilterOptionsValue("saleChannelId", []);
    vehicleDashboardChangeFilterOptionsValue("searchFilter", "");
  };

  useEffect(() => {
    userSessionMenuItemActive(MenuItemActive.VEHICLE_DASHBOARD);
    tokenService.authenticateToken();
  }, []);

  useEffect(() => {
    if (registerActive?.id) {
      form.setFieldsValue(registerActive);
    }
  }, [registerActive]);

  useEffect(() => {
    resetFilterVehicleDashboard();
    lobbyInboundProcessesFetchAsync();
    lobbySaleChannelsFetchAsync();
  }, []);

  useEffect(() => {
    intervalCallApi();
    const timer = setInterval(() => intervalCallApi(), INTERVAL_MINUTES);
    return () => {
      clearInterval(timer);
      notification.destroy();
    };
  }, []);

  useEffect(() => {
    if (!visibleDrawerViewRegisterVehicleDashboard) {
      lobbyUpdateRegisterActive({ registerActive: null });
    }
    if (!visibleDrawerViewCheckinRegisterVehicleDashboard) {
      lobbyCheckinUpdateRegisterActive({ checkinRegisterActive: null });
    }
    form.resetFields();
  }, [visibleDrawerViewRegisterVehicleDashboard, visibleDrawerViewCheckinRegisterVehicleDashboard]);

  const handleCloseViewDrawerInUnit = () => {
    vehicleDashboardHideDrawerViewRegister();
    form.resetFields();
    lobbyClearDocuments();
    lobbyCheckinClearDrivers();
  };

  const handleCloseViewDrawerCheckin = () => {
    vehicleDashboardHideDrawerViewCheckinRegister();
    form.resetFields();
    lobbyCheckinClearDrivers();
    lobbyClearDocuments();
  };

  return (
    <Layout title={TITLE_PAGE_REGISTRATION} isLoading={isLoading} role={userData.role}>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <S.LayoutContent>
          <FilterOptions
            salesChannelList={salesChannelList}
            allProcessesList={inboundProcessesList}
            processValue={processValue}
            setProcessValue={setProcessValue}
            salesChannelValue={salesChannelValue}
            setSalesChannelValue={setSalesChannelValue}
            vehicleDashboardChangeFilterOptionsValue={vehicleDashboardChangeFilterOptionsValue}
            vehicleDashboardFetchRegisterAsync={vehicleDashboardFetchRegisterAsync}
            vehicleDashboardResetVehiclesData={vehicleDashboardResetVehiclesData}
          />
        </S.LayoutContent>
        <S.LayoutContent>
          <VehiclesCard
            registers={items?.VehicleArriving?.registers}
            description={PRESENTEND}
            totalRegisters={items?.VehicleArriving?.totalItems.toString()}
            isLoadingRegisters={isLoading}
            lobbyFetchItemAsync={lobbyFetchItemAsync}
            lobbyFetchItemCheckinAsync={lobbyFetchItemCheckinAsync}
            vehicleDashboardShowDrawerViewCheckinRegister={vehicleDashboardShowDrawerViewCheckinRegister}
            vehicleDashboardShowDrawerViewRegister={vehicleDashboardShowDrawerViewRegister}
            vehicleCardInformation={VehicleArrivingInformations()}
            hasReleased={false}
            hasRfidReadingActive={hasRfidReadingActive}
          />
        </S.LayoutContent>
        <S.LayoutContent>
          <VehiclesCard
            registers={items?.InRadius?.registers}
            description={IN_RADIUS}
            totalRegisters={items?.InRadius?.totalItems.toString()}
            isLoadingRegisters={isLoading}
            lobbyFetchItemAsync={lobbyFetchItemAsync}
            lobbyFetchItemCheckinAsync={lobbyFetchItemCheckinAsync}
            vehicleDashboardShowDrawerViewCheckinRegister={vehicleDashboardShowDrawerViewCheckinRegister}
            vehicleDashboardShowDrawerViewRegister={vehicleDashboardShowDrawerViewRegister}
            vehicleCardInformation={InRadiusInformations()}
            hasReleased={false}
            hasRfidReadingActive={hasRfidReadingActive}
          />
        </S.LayoutContent>
        <S.LayoutContent>
          <VehiclesCard
            registers={items?.InRadiusWithoutCheckin?.registers}
            description={NO_CHECK_IN_RADIUS}
            totalRegisters={items?.InRadiusWithoutCheckin?.totalItems.toString()}
            isLoadingRegisters={isLoading}
            lobbyFetchItemAsync={lobbyFetchItemAsync}
            lobbyFetchItemCheckinAsync={lobbyFetchItemCheckinAsync}
            vehicleDashboardShowDrawerViewCheckinRegister={vehicleDashboardShowDrawerViewCheckinRegister}
            vehicleDashboardShowDrawerViewRegister={vehicleDashboardShowDrawerViewRegister}
            vehicleCardInformation={NoCheckinInRadiusInformations()}
            hasReleased={false}
            hasRfidReadingActive={hasRfidReadingActive}
          />
        </S.LayoutContent>
        <S.LayoutContent>
          <VehiclesCard
            registers={items?.ReleasedOrNotReleased?.registers}
            description={`${RELEASED} / ${NOT_RELEASED}`}
            totalRegisters={items?.ReleasedOrNotReleased?.totalItems.toString()}
            isLoadingRegisters={isLoading}
            lobbyFetchItemAsync={lobbyFetchItemAsync}
            lobbyFetchItemCheckinAsync={lobbyFetchItemCheckinAsync}
            vehicleDashboardShowDrawerViewCheckinRegister={vehicleDashboardShowDrawerViewCheckinRegister}
            vehicleDashboardShowDrawerViewRegister={vehicleDashboardShowDrawerViewRegister}
            vehicleCardInformation={ReleasedNotReleasedInformations()}
            hasReleased
            hasRfidReadingActive={hasRfidReadingActive}
          />
        </S.LayoutContent>
        <S.LayoutContent>
          <VehiclesCard
            registers={items?.InternQueue?.registers}
            description={INTERN_QUEUE}
            totalLengthOfStay={items?.InternQueue?.averageStay}
            totalRegisters={items?.InternQueue?.totalItems.toString()}
            isLoadingRegisters={isLoading}
            lobbyFetchItemAsync={lobbyFetchItemAsync}
            lobbyFetchItemCheckinAsync={lobbyFetchItemCheckinAsync}
            vehicleDashboardShowDrawerViewCheckinRegister={vehicleDashboardShowDrawerViewCheckinRegister}
            vehicleDashboardShowDrawerViewRegister={vehicleDashboardShowDrawerViewRegister}
            vehicleCardInformation={InternQueueInformations()}
            hasReleased={false}
            hasRfidReadingActive={hasRfidReadingActive}
          />
        </S.LayoutContent>
        <S.LayoutContent>
          <VehiclesCard
            registers={items?.InOperation?.registers}
            description={IN_OPERATION}
            totalRegisters={items?.InOperation?.totalItems.toString()}
            isLoadingRegisters={isLoading}
            lobbyFetchItemAsync={lobbyFetchItemAsync}
            lobbyFetchItemCheckinAsync={lobbyFetchItemCheckinAsync}
            vehicleDashboardShowDrawerViewCheckinRegister={vehicleDashboardShowDrawerViewCheckinRegister}
            vehicleDashboardShowDrawerViewRegister={vehicleDashboardShowDrawerViewRegister}
            vehicleCardInformation={InOperationInformations()}
            hasReleased={false}
            hasRfidReadingActive={hasRfidReadingActive}
          />
        </S.LayoutContent>
      </div>
      <YmsDrawer
        title={VIEW_ENTRY}
        placement="right"
        icon={<ShippingTruck style={S.ShippingTruckIcon} />}
        closable={false}
        open={visibleDrawerViewRegisterVehicleDashboard}
        width="900"
        cancelbuttontxt={CANCEL}
        showConfirmButton={false}
        maskClosable={true}
        destroyOnClose={true}
        disabled={drawerIsLoading}
        onClose={handleCloseViewDrawerInUnit}
      >
        <ViewRegister
          form={form}
          documentsList={documentsList}
          inboundProcessesList={inboundProcessesList}
          lobbyDocumentsFetchAsync={lobbyDocumentsFetchAsync}
          visibleDrawerViewRegisterVehicleDashboard={visibleDrawerViewRegisterVehicleDashboard}
          hasSaleChannel={hasSaleChannel}
          salesChannelList={salesChannelList}
          lobbyProcessActive={lobbyProcessActive}
          lobbySaleChannelActive={lobbySaleChannelActive}
          drawerIsLoading={drawerIsLoading}
        />
      </YmsDrawer>
      <YmsDrawer
        title={VIEW_ENTRY}
        placement="right"
        icon={<ShippingTruck style={S.ShippingTruckIcon} />}
        closable={false}
        open={visibleDrawerViewCheckinRegisterVehicleDashboard}
        width="900"
        cancelbuttontxt={CANCEL}
        maskClosable={true}
        showConfirmButton={false}
        disabled={drawerIsLoading}
        onClose={handleCloseViewDrawerCheckin}
        destroyOnClose={true}
      >
        <FormCheckin
          form={form}
          documentsList={documentsList}
          lobbyDocumentsFetchAsync={lobbyDocumentsFetchAsync}
          visibleDrawerCheckinRegister={visibleDrawerViewCheckinRegisterVehicleDashboard}
          lobbyProcessActive={lobbyProcessActive}
          inboundProcessesList={inboundProcessesList}
          hasSaleChannel={hasSaleChannel}
          salesChannelList={salesChannelList}
          lobbySaleChannelActive={lobbySaleChannelActive}
          hasItems={false}
          drawerIsLoading={drawerIsLoading}
          documentType={documentType}
          hasEntryPermission={false}
        />
      </YmsDrawer>
    </Layout>
  );
};

const mapDispatchToActions = (dispatch: any) => bindActionCreators({ ...VehicleDashboardActions, ...UserSessionActions, ...LobbyActions }, dispatch);

const mapStateToProps = (state) => ({
  isLoading: state.VehicleDashboard.isLoading,
  items: state.VehicleDashboard.items,
  visibleDrawerViewRegisterVehicleDashboard: state.VehicleDashboard.visibleDrawerViewRegisterVehicleDashboard,
  visibleDrawerViewCheckinRegisterVehicleDashboard: state.VehicleDashboard.visibleDrawerViewCheckinRegisterVehicleDashboard,
  userData: state.UserSession.userData,
  registerActive: state.Lobby.registerActive,
  drawerIsLoading: state.Lobby.drawerIsLoading,
  salesChannelList: state.Lobby.salesChannelList,
  hasSaleChannel: state.Lobby.hasSaleChannel,
  inboundProcessesList: state.Lobby.inboundProcessesList,
  documentsList: state.Lobby.documentsList,
  documentType: state.Lobby.documentType,
  processId: state.VehicleDashboard.filterOptions.processId,
  saleChannelId: state.VehicleDashboard.filterOptions.saleChannelId,
  hasRfidReadingActive: state.Lobby.hasRfidReading,
});

export default connect(mapStateToProps, mapDispatchToActions)(VehicleDashboard);
